import React from "react";
import Form from "react-bootstrap/Form";
import "./template-creation.scss";
import DropdownMain from "../../../../../components/dropdown-main/DropdownMain";
import { FluentTime } from "../../../../../components/FluentUI/Fluent-Time";
import {
  ReminderEnum,
  reminderRecurringIntervalTypeValues,
  reminderRecurringValues,
  reminderValues,
} from "../template-utility";
import { getTimeFromTimeStamp } from "../../../../../util/time-helpers";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import { FluentSwitch } from "../../../../../components/FluentUI/Fluent-Switch";
import { FluentDropdownOptionAsObject } from "../../../../../components/FluentUI/Fluent-Dropdown-Option-As-Object";
import { FluentComboboxOptionAsObject } from "../../../../../components/FluentUI/Fluent-Combobox-Option-As-Object";
import { renderLabelWithInfoTooltip } from "../../../../../util/general-helpers";
import PropTypes from "prop-types";

const TemplateCreationFormRightBottomNew = ({
  values,
  setFieldValue,
  errors,
  departments,
  users,
  updateOwners,
  updateMembers,
  isSuperAdmin,
  handleDepartmentChange,
  onMultiOwnerToggle,
  isEdit = false,
  initialMultiOwner = false
}) => {
  const isMultiOwner = values?.IsMultiOwner;

  const showhideIsCreateNewUserTasksCurrent = () => {
    if (values?.IsCreateNewUserTasks === false) {
      setFieldValue("IsCreateNewUserTasksCurrent", false);
    } else {
    }
  };

  const handleMultiOwnerToggle = (checked) => {
    if (checked && onMultiOwnerToggle) {
      onMultiOwnerToggle(true);
    }
    setFieldValue("IsMultiOwner", checked);
    if (!checked && values?.DefaultMembership) {
      const ownerCount = values.Owners?.length || 0;
      
      if (ownerCount > 1) {
        setFieldValue("Owners", []);
        updateOwners?.([]);
      }
    }
  };

  const reorderOptions = (options, selected) => {
    const selectedSet = new Set(selected.map(String));
    const selectedOptions = options.filter(o => selectedSet.has(String(o.id)));
    const unselectedOptions = options.filter(o => !selectedSet.has(String(o.id)));
    return [...selectedOptions, ...unselectedOptions];
  };
  return (
    <div className="task-accordion-container">
      <div className="heading toggle--row">
        <div className="heading-description toggle-description toggle--label">
          <FluentUiIcon
            name="PeopleTeamRegular"
            color="#0F6CBD"
            size={20}
          />
          {renderLabelWithInfoTooltip(
            "Allow Multiple Project Owners",
            <>
              When this setting is flipped 'On', you can assign multiple default Project Owners.
              <br /><br />
              Projects created using this Template will also support multiple Project Owners.
              <br /><br />
              This change is <strong>permanent</strong> and cannot be undone once saved.
            </>
          )}
        </div>

        <FluentSwitch
          label=""
          checked={values?.IsMultiOwner === true}
          disabled={isEdit && initialMultiOwner === true}
          onChange={(_, data) => {
            handleMultiOwnerToggle(data.checked);
          }}
          size="medium"
        />
      </div>
      <div className="heading toggle--row">
        <div className="heading-description toggle-description toggle--label">
          <FluentUiIcon
            name="PeopleRegular"
            color="#0F6CBD"
            size={20}
          />
          {renderLabelWithInfoTooltip(
            "Default Memberships",
            <>
              When this setting is flipped 'On', you can set a default Project Owner and Members.
              <br /><br />
              On using this Template during Project creation, these defaults will be automatically applied — saving you time on setup.
            </>
          )}
        </div>

        <FluentSwitch
          label=""
          checked={values?.DefaultMembership === true}
          onChange={(_, data) => {
            setFieldValue("DefaultMembership", data.checked);
            if (!data.checked) {
              setFieldValue("Owners", []);
              setFieldValue("Members", []);
              updateOwners?.([]);
              updateMembers?.([]);
            }
          }}
          size="medium"
        />
      </div>

      {values?.DefaultMembership && (
        <>
          <div style={{ paddingBottom: "16px" }}>
            <FluentDropdownOptionAsObject
              key={`dept-${String(values.CompanyId)}-${departments?.length || 0}`}
              label="Department"
              placeholder="Select a department"
              isControlled={true}
              disabled={!isSuperAdmin}
              options={departments.map(d => ({
                id: String(d.Id),
                name: d.Name,
              }))}
              value={departments.find(d => d.Id === values.CompanyId)?.Name ?? ""}
              selectedOptions={
                values.CompanyId != null ? [String(values.CompanyId)] : []
              }
              onChange={(_, data) => {
                const id = data.optionValue;
                const sel = departments.find(d => String(d.Id) === id);
                if (sel) {
                  setFieldValue("CompanyId", sel.Id);
                  setFieldValue("Owners", []);
                  setFieldValue("Members", []);
                  handleDepartmentChange(id);
                }
              }}
            />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <FluentComboboxOptionAsObject
              label="Default Project Owner"
              placeholder="Search a User"
              showAvatars
              avatarColor="brand"
              options={reorderOptions(
                (users || []).map(u => ({ id: u.Id, name: u.Name })),
                (values.Owners || []).map(String)
              )}
              selectedOptions={(values.Owners || []).map(String)}
              onSelectionChange={(ids) => {
                const nums = ids.map((x) => Number(x));
                setFieldValue("Owners", nums);
                updateOwners?.(nums);
              }}
              {...(isMultiOwner && { multiselect: true, showPills: true })}
            />
            <div className="heading toggle--row" style={{paddingTop: "8px"}}>
              <div className="heading-description description toggle--label">
                {renderLabelWithInfoTooltip(
                  "Disable Project Owner Transfers",
                  <>
                    When this setting is flipped 'On', you can disable Project Owner Transfers during Project Creation. Project Ownership can only be transferred after Project Creation.
                  </>
                )}
              </div>

              <FluentSwitch
                label=""
                checked={values?.CanTransferOwner === false}
                onChange={(_, data) => {
                  setFieldValue("CanTransferOwner", !data.checked);
                }}
                size="medium"
              />
            </div>
          </div>

          <div style={{ marginBottom: "16px" }}>
          <FluentComboboxOptionAsObject
            label="Default Project Members"
            placeholder="Search a User"
            multiselect
            showAvatars
            avatarColor="blue"
            options={reorderOptions(
              (users || []).map(u => ({ id: u.Id, name: u.Name })),
              (values.Members || []).map(String)
            )}
            selectedOptions={(values.Members || []).map(String)}
            onSelectionChange={(ids) => {
              const nums = ids.map((x) => Number(x));
              setFieldValue("Members", nums);
              updateMembers?.(nums);
            }}
            showPills
          />
          </div>
        </>
      )}

      <div className="heading toggle--row">
        <div className="heading-description toggle-description toggle--label">
          <FluentUiIcon
            name="Alert20Regular"
            color="#0F6CBD"
            size={20}
          />
          <h4 className="heading-name">Set Project Reminders for Owner</h4>
        </div>

        <FluentSwitch
          label=""
          checked={values?.Reminder != null}
          onChange={(_, data) => {
            const value = data.checked ? reminderValues[0] : null;
            setFieldValue("Reminder", value);
          }}
          size="medium"
        />
      </div>

      {values?.Reminder != null && (
        <>
          <div className="elements-container">
            <div className="left-element">
              <div className="bool-container">
                {/* on due date checkbox */}
                <div
                  onClick={(e) => {
                    setFieldValue("Reminder", {
                      id: "0",
                      name: "On End Date",
                    });
                  }}
                >
                  <Form.Check
                    type="checkbox"
                    name="allApprovers"
                    checked={values?.Reminder?.id === ReminderEnum.OnEndDate}
                    label="On Due Date"
                    id="onDueDateCheckbox"
                  />
                </div>

                {/* one-time checkbox */}
                <div
                  onClick={(e) => {
                    setFieldValue("Reminder", {
                      id: "2",
                      name: "Before Due Date",
                    });
                  }}
                >
                  <Form.Check
                    type="checkbox"
                    name="allApprovers"
                    checked={
                      values?.Reminder?.id === ReminderEnum.BeforeDueDate ||
                      values?.Reminder?.id === ReminderEnum.AfterStartDate
                    }
                    label="One-Time"
                    id="oneTimeCheckbox"
                  />
                </div>

                {/* recurring checkbox */}
                <div
                  onClick={(e) => {
                    setFieldValue("Reminder", {
                      id: "1",
                      name: "Recurring",
                    });
                  }}
                >
                  <Form.Check
                    type="checkbox"
                    name="allApprovers"
                    checked={values?.Reminder?.id === ReminderEnum.Recurring}
                    label="Recurring"
                    id="recurringCheckbox"
                  />
                </div>
              </div>
            </div>
          </div>

          {/*on due date configuration*/}
          <div
            className={`elements-container ${
              values?.Reminder?.id === ReminderEnum.OnEndDate
                ? "show"
                : "no-show"
            }`}
          >
            <FluentTime
              className="big-element"
              value={values?.ReminderOneTimeTime}
              onChange={(value) => {
                setFieldValue("ReminderOneTimeTime", value);
              }}
              timeIntervals={15}
            />
          </div>

          {/*one time configuration*/}
          {(values?.Reminder?.id === ReminderEnum.BeforeDueDate ||
            values?.Reminder?.id === ReminderEnum.AfterStartDate) && (
            <div className="one-time-recurrence-container">
              <input
                id="reminderDays"
                name="reminderDays"
                value={values?.ReminderOneTimeDaysBefore}
                type="number"
                min={1}
                required
                onChange={(e) => {
                  setFieldValue("ReminderOneTimeDaysBefore", e.target.value);
                }}
              />

              <h4>days</h4>

              <div className="reminder-dropdown">
                <DropdownMain
                  values={reminderValues}
                  dropdownButtonText={"-Select-"}
                  onSelect={(e) => {
                    setFieldValue("Reminder", e);
                  }}
                  currentSelected={values?.Reminder}
                />
              </div>

              <h4>at</h4>

              <div className="reminder-date-picker">
                <FluentTime
                  value={values?.ReminderOneTimeTime}
                  onChange={(value) => {
                    setFieldValue("ReminderOneTimeTime", value);
                  }}
                  timeIntervals={15}
                />
              </div>
            </div>
          )}

          <Form.Control.Feedback type="invalid" className="inline-error">
            {errors.ReminderOneTimeDaysBefore}
          </Form.Control.Feedback>

          {/*recurring configuration*/}
          <div
            className={`elements-container ${
              values?.Reminder?.id === ReminderEnum.Recurring
                ? "show"
                : "no-show"
            }`}
          >
            <div className="left-element">
              <DropdownMain
                values={reminderRecurringValues}
                dropdownButtonText={"-Select-"}
                onSelect={(e) => {
                  setFieldValue("ReminderRecurring", e.name);
                }}
                currentSelected={reminderRecurringValues.find(
                  (val) => val.name === values?.ReminderRecurring
                )}
              />
            </div>
            <div className="mid-element"></div>

            <div className="right-element">
              <FluentTime
                value={values?.ReminderRecurringTime}
                onChange={(value) => {
                  setFieldValue("ReminderRecurringTime", value);
                }}
                timeIntervals={15}
              />
            </div>
          </div>

          {/* recurring custom configuration */}
          {values?.Reminder?.id === ReminderEnum.Recurring &&
            values?.ReminderRecurring === "Custom" && (
              <div className="elements-container">
                <div className="left-element">
                  <div className="row-container">
                    <h4>Repeat every</h4>
                    <input
                      id="reminderDays"
                      name="reminderDays"
                      value={values?.ReminderRecurringIntervalValue}
                      type="number"
                      min={1}
                      required
                      onChange={(e) => {
                        setFieldValue(
                          "ReminderRecurringIntervalValue",
                          e.target.value
                        );
                      }}
                    />
                    <div className="row-dropdown">
                      <DropdownMain
                        values={reminderRecurringIntervalTypeValues}
                        dropdownButtonText={"-Select-"}
                        onSelect={(e) => {
                          setFieldValue(
                            "ReminderRecurringIntervalType",
                            e.name
                          );
                        }}
                        currentSelected={reminderRecurringIntervalTypeValues.find(
                          (val) =>
                            val.name === values?.ReminderRecurringIntervalType
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* reminder information text*/}
          <h4 className="recurrence-details">
            Reminder will be sent
            {values?.Reminder?.id === ReminderEnum.OnEndDate && " on due date "}
            {values?.Reminder?.id === ReminderEnum.Recurring &&
              (values?.ReminderRecurring === "Custom"
                ? ` every ${
                    values?.ReminderRecurringIntervalValue
                  } ${values?.ReminderRecurringIntervalType.toLowerCase()} after start date`
                : ` ${values?.ReminderRecurring.toLowerCase()}`)}
            {(values?.Reminder?.id === ReminderEnum.BeforeDueDate ||
              values?.Reminder?.id === ReminderEnum.AfterStartDate) &&
              ` ${
                values?.ReminderOneTimeDaysBefore
              } days ${values?.Reminder?.name.toLowerCase()} `}
            {" at " +
              getTimeFromTimeStamp(
                values?.Reminder?.id === ReminderEnum.Recurring
                  ? values?.ReminderRecurringTime
                  : values?.ReminderOneTimeTime
              )}
          </h4>
        </>
      )}

      <div className="heading toggle--row" style={{ marginBottom: "16px" }}>
        <div className="heading-description toggle-description toggle--label">
          <FluentUiIcon
            name="ClipboardTextLtrRegular"
            color="#0F6CBD"
            size={20}
          />
          {renderLabelWithInfoTooltip(
            "Add New Project Members to Existing Tasks",
            "If the Project has Non-Individual Recurring Tasks"
          )}
        </div>

        <FluentSwitch
          label=""
          checked={values?.IsCreateNewUserTasks}
          onChange={(_, data) => {
            setFieldValue("IsCreateNewUserTasks", data.checked);
            showhideIsCreateNewUserTasksCurrent();
          }}
          size="medium"
        />
      </div>

      {values?.IsCreateNewUserTasks && (
          <div className="heading toggle--row" style={{ marginBottom: "16px" }}>
            <div className="heading-description toggle-description toggle--label">
              <FluentUiIcon
                name="ClipboardTextLtrRegular"
                color="#0F6CBD"
                size={20}
              />
              {renderLabelWithInfoTooltip(
                "Add New Project Members to Existing Tasks Immediately",
                "If the Project has Non-Individual Recurring Tasks"
              )}
            </div>

            <FluentSwitch
              label=""
              checked={values?.IsCreateNewUserTasksCurrent}
              onChange={(_, data) => {
                setFieldValue("IsCreateNewUserTasksCurrent", data.checked);
              }}
              size="medium"
            />
          </div>
      )}
    </div>
  );
};

TemplateCreationFormRightBottomNew.propTypes = {
  values: PropTypes.object,
  setFieldValue: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  departments: PropTypes.array.isRequired,
  users: PropTypes.array.isRequired,
  updateOwners: PropTypes.func.isRequired,
  updateMembers: PropTypes.func.isRequired,
  isSuperAdmin: PropTypes.bool.isRequired,
  handleDepartmentChange: PropTypes.func.isRequired,
  onMultiOwnerToggle: PropTypes.func,
  isEdit: PropTypes.bool,
  initialMultiOwner: PropTypes.bool,
};
export default TemplateCreationFormRightBottomNew;
